"use client";

import { OnboardingItemLayout } from "@/components/Onboarding/components/OnboardingItemLayout";
import { Button } from "@/components/ui/button";
import {
  Action,
  ControlType,
  useOnboardingAnalytics,
} from "@/context/AnalyticsProvider";
import { useOnboardingStep } from "@/context/OnboardingStepProvider/OnboardingStepProvider";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef } from "react";
import { OnboardingStep, type OnboardingStepProps } from "../constants";

export const ExploreGamesInfo = ({ visible }: OnboardingStepProps) => {
  const { goToNextStep, isModal } = useOnboardingStep();
  const { currentStep } = useOnboardingStep();
  const hasViewedRef = useRef(false);
  const { track } = useOnboardingAnalytics();

  useEffect(() => {
    if (hasViewedRef.current || !visible) return;
    hasViewedRef.current = true;

    track({
      action: Action.VIEWED,
      extras: {
        id: OnboardingStep.EXPLORE_GAMES_INFO,
        step: currentStep,
      },
    });
  }, [track, visible, currentStep]);

  const handleButtonClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.EXPLORE_GAMES_INFO,
        step: currentStep,
        control: "next-step",
        controlType: ControlType.BUTTON,
      },
    });

    goToNextStep();
  };

  const handleLinkClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.EXPLORE_GAMES_INFO,
        step: currentStep,
        control: "continue-to-immutable-play",
        controlType: ControlType.LINK,
      },
    });
  };

  const handleImageClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.EXPLORE_GAMES_INFO,
        step: currentStep,
        control: "explore-games",
        controlType: ControlType.IMAGE,
      },
    });
  };

  return (
    <OnboardingItemLayout className="px-0 py-4">
      <div className="text-center">
        <h1 className="imx-gradient font-bold text-sm tracking-wider">
          HOW IT WORKS
        </h1>

        <h2 className="font-bold text-3xl">Explore games</h2>

        <p className="mt-2">
          Check out these amazing titles <br />
          <span className="italic">and more</span> on Immutable Play!
        </p>
      </div>

      <Image
        src="/images/onboarding/explore-games-characters.webp"
        alt="Explore games"
        width={412}
        height={369}
        className="w-full min-h-[37.5dvh] object-contain"
        onClick={handleImageClick}
        priority
      />

      <div className="flex flex-col items-center gap-y-4 w-full px-6">
        <Button
          size="lg"
          className="w-full rounded-full py-4 text-base font-semibold relative"
          onClick={handleButtonClick}
        >
          Next step
          <ArrowRight className="absolute size-5 right-4" />
        </Button>

        {!isModal ? (
          <Link
            href="/quests"
            className="text-text-body-secondary underline"
            onClick={handleLinkClick}
          >
            Continue to Immutable Play
          </Link>
        ) : null}
      </div>
    </OnboardingItemLayout>
  );
};
